import React, { FC, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { Link } from 'react-router-dom';

import useTranslate from '../../hooks/useTranslate';
import getGolfClubInfo from '../../services/apis/golfCourses/getGolfClubInfo';
import getGolfCourseUrl from '../../services/apis/golfCourses/getGolfCourseUrl';
import { GolfClubPromotion, MappedGolfCourseType } from '../../services/apis/golfCourses/types';
import useStore from '../../store/useStore';
import Button from '../Button/Button';
import Carousel from '../Carousel/Carousel';
import FullscreenModal from '../FullscreenModal/FullscreenModal';
import GolfCourseBadge from '../GolfCourseBadge/GolfCourseBadge';
import SanitizeHTML from '../SanitizeHTML/SanitizeHTML';
import Text from '../Text/Text';
import RatingBox from './RatingBox';

type GolfCourseProps = {
  golfCourse: MappedGolfCourseType;
  onShowMoreClick?: () => void;
  topRated?: boolean;
  topBooked?: boolean;
  discount?: boolean;
  discountValue?: number;
};

const GolfCourse: FC<GolfCourseProps> = props => {
  const {
    golfCourse,
    topRated = false,
    topBooked = false,
    onShowMoreClick,
    discount,
    discountValue,
  } = props;
  const { translate, activeLanguage } = useTranslate();

  const [modalOpened, setModalOpened] = useState(false);
  const isGolfhaftetRequest = useStore(state => state.isGolfhaftetRequest);

  const distanceText = golfCourse.distance ? `(${Math.round(golfCourse.distance)} km)` : '';
  const info = getGolfClubInfo(golfCourse);

  return (
    <>
      <div>
        {info?.image && (
          <div className={'position-relative'}>
            <a
              href={info.image}
              target="_blank"
              rel="noreferrer"
              onClick={e => {
                e.preventDefault();
                setModalOpened(true);
              }}
              className="mb-4 d-block ratio ratio-16x9"
              style={{ cursor: 'zoom-in', maxHeight: '300px' }}
            >
              <img alt={golfCourse.name} src={info.image} className="d-block img-fluid rounded-3" />
            </a>
            {!isGolfhaftetRequest && (
              <GolfCourseBadge
                className={'position-absolute top-0 end-0'}
                topRated={topRated}
                topBooked={topBooked}
                promoted={golfCourse.promotion === GolfClubPromotion.Promoted}
                recommended={golfCourse.promotion === GolfClubPromotion.Recommended}
                discount={
                  discount !== undefined ? discount : !!golfCourse.golfCourse.campaignPercentage
                }
                discountValue={
                  discountValue !== undefined
                    ? discountValue
                    : golfCourse.golfCourse.campaignPercentage
                }
              />
            )}
            <FullscreenModal visible={modalOpened} close={() => setModalOpened(false)}>
              {info?.photos.length > 0 ? (
                <Carousel
                  items={info.photos.map(item => {
                    return { src: item.assetPath, alt: item.name };
                  })}
                />
              ) : (
                <img alt={golfCourse.name} src={info.image} className="d-block" />
              )}
            </FullscreenModal>
          </div>
        )}
      </div>
      <div>
        <h2>{golfCourse.golfClub.name}</h2>

        <h3>
          {golfCourse.golfCourse.name} {distanceText}
        </h3>

        <RatingBox course={golfCourse} />

        <div className="mt-3">
          {info?.description ? (
            <div>
              <Text clamp={2}>
                <SanitizeHTML>{info.description}</SanitizeHTML>
              </Text>
            </div>
          ) : (
            <Translate id="default.CourseDescriptionMissing" />
          )}
        </div>
        <div className="text-end">
          <Link to={`/${activeLanguage.code}/booking/${getGolfCourseUrl(golfCourse)}`}>
            {translate('golfClubPage.expandDescriptionCTA')}
          </Link>
        </div>
      </div>
      {onShowMoreClick && (
        <div className="text-right mt-auto">
          <Button primary className="my-3" onClick={onShowMoreClick}>
            <Translate id="default.ShowAvailability" />
          </Button>
        </div>
      )}
    </>
  );
};

export default GolfCourse;
